import { cloneElement, useMemo } from 'react';

import { Alert, ThemeProvider } from '@change/ds-core-ui/next';

import { useI18n } from '@change-corgi/core/react/i18n';
import { FormError } from '@change-corgi/design-system-legacy/components/forms';
import { Flex } from '@change-corgi/design-system-legacy/layout';

import { useStripeForm } from '../v2/hooks/useStripeForm';

import { SingleForm } from './components/SingleForm';
import { SplitForm } from './components/SplitForm';
import type { StripeCreditCardLayoutProps } from './types';

export function StripeCreditCardLayout({
	submitButton,
	children,
	labelWeight,
	displayErrorInAlert,
	phoneNumberInput,
	...rest
}: StripeCreditCardLayoutProps): JSX.Element {
	const {
		data: { errorMessage, validationErrors, errorType },
		actions: { handleSubmit, validateStripeElementOnChange, setValidationErrors },
	} = useStripeForm(rest);

	const { translate } = useI18n();

	const { errorTitle, errorDescription } = useMemo(() => {
		if (errorType === 'api_connection_error') {
			return {
				errorTitle: translate('corgi.payments.stripe_error.connection_error_heading'),
				errorDescription: errorMessage || translate('corgi.payments.stripe_error.generic_error_description'),
			};
		}
		return {
			errorTitle: translate('corgi.payments.stripe_error.generic_decline_error_heading'),
			errorDescription: translate('corgi.payments.stripe_error.generic_error_description'),
		};
	}, [errorType, errorMessage, translate]);

	return (
		<form onSubmit={handleSubmit}>
			<Flex sx={{ flexDirection: 'column', gap: 16 }}>
				{children}
				{rest.layout === 'single' ? (
					<SingleForm
						{...rest}
						labelWeight={labelWeight}
						validationErrors={validationErrors}
						validateStripeElementOnChange={validateStripeElementOnChange}
						setValidationErrors={setValidationErrors}
					/>
				) : (
					<SplitForm
						{...rest}
						validationErrors={validationErrors}
						validateStripeElementOnChange={validateStripeElementOnChange}
					/>
				)}
				{displayErrorInAlert && errorMessage && (
					<ThemeProvider>
						<Alert variant="error" heading={errorTitle} data-qa="stripe-form-error-message">
							{errorDescription}
						</Alert>
					</ThemeProvider>
				)}
				{phoneNumberInput}
				<Flex sx={{ justifyContent: 'center' }}>
					{cloneElement(submitButton, {
						disabled: submitButton.props.disabled,
						loading: submitButton.props.loading,
					})}
				</Flex>
				{!displayErrorInAlert && errorMessage && (
					<FormError data-qa="stripe-form-error-message">{errorMessage}</FormError>
				)}
			</Flex>
		</form>
	);
}
